<script setup lang="ts">
import { watch, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
import { each, isObject } from 'lodash';
import uPlot from 'uplot';
import { optionsUpdateState, chartDataMatches } from './helpers';

const { stepped } = uPlot.paths!;

function createOptions(options: uPlot.Options): uPlot.Options {
  each(options.series, (ser) => {
    const steppedOptions = (ser as any).stepped;
    if (stepped && isObject(steppedOptions)) {
      ser.paths = stepped(
        steppedOptions as uPlot.Series.SteppedPathBuilderOpts,
      );
    }
  });
  return options;
}

const props = defineProps<{
  id?: string;
  options: uPlot.Options;
  chartData: uPlot.AlignedData;
}>();

const containerRef = useTemplateRef<HTMLDivElement>('container');

let _chart: uPlot | undefined;
let _options: uPlot.Options | null = null;

function _create() {
  if (_options == null) {
    _options = createOptions(props.options);
  }
  if (containerRef.value) {
    _chart = new uPlot(_options, props.chartData, containerRef.value);
  }
}

function _destroy() {
  if (_chart) {
    _chart.destroy();
    _chart = undefined;
  }
}

watch(
  () => props.options,
  (current, previous) => {
    _options = createOptions(current);

    if (previous) {
      const optionsState = optionsUpdateState(previous, current);
      if (!_chart || optionsState === 'create') {
        _destroy();
        _create();
      } else if (optionsState === 'update') {
        _chart.setSize({ width: current.width, height: current.height });
      }
    }
  },
);

watch(
  () => props.chartData,
  (current, previous) => {
    if (!_chart) {
      _create();
    } else if (previous && !chartDataMatches(previous, current)) {
      _chart.setData(current);
    }
  },
);

function onMouseEnter() {
  if (_chart) {
    _chart.syncRect();
  }
}

onMounted(() => {
  _create();
});

onBeforeUnmount(() => {
  _destroy();
});
</script>

<template>
  <div ref="container" @mouseenter="onMouseEnter"></div>
</template>
